/**
 * masterGanttMachineRows.ts
 * REQ-03 — Machine-grouped layout for the Master Gantt.
 *
 * The default Master Gantt groups Project → WorkOrder → Machines/Staff (see
 * masterGanttRowBuilder.ts). This module provides the alternative grouping the
 * lab user asked for: group BY MACHINE first, then nest each machine's work
 * under the PROJECT and WORK ORDER it belongs to, with the PEOPLE who worked
 * that work order on that machine in a sub-group beneath the bookings.
 *
 * Row hierarchy (Highcharts treegrid via `id` / `parent` on each point):
 *
 *   Machine row              id = "mach-{machineKey}"                        (top level)
 *     └─ Project row         id = "machproj-{machineKey}-{projectKey}",   parent = "mach-{machineKey}"
 *          └─ Work Order row id = "machwo-{machineKey}-{workOrderKey}",   parent = "machproj-{machineKey}-{projectKey}"
 *               ├─ Booking n id = "booking-{bookingKey}",                parent = "machwo-{machineKey}-{workOrderKey}"  (FIRST)
 *               └─ People    id = "machwopeople-{machineKey}-{workOrderKey}", parent = "machwo-{machineKey}-{workOrderKey}"  (after bookings)
 *                    └─ Person  id = "machwoperson-{m}-{wo}-{userKey}"  (collapsed; BUG-2026-0194 — one row per person)
 *                         └─ Task bar id = "assign-{firstWeeklyKey}"    (weekly rows merged per task)
 *                    (a person with a single task is the task bar itself, directly under People)
 *
 *   Unassigned-to-machine band (only when some assignment can't be linked to a
 *   machine — see "Linking assignments to machines" below):
 *     People band            id = "people-root"                (top level)
 *       └─ Person row        id = "people-{userKey}",      parent = "people-root"
 *            └─ Task bar     id = "assign-{firstWeeklyKey}", parent = "people-{userKey}"  (weeks merged)
 *
 * BUG-2026-0194: people used to appear once per WEEKLY assignment row (one
 * block per week per task). The People band now lists each person once (their
 * envelope = all their weeks on this machine/WO) with a drill-down into one bar
 * per task; masterGanttAssignmentMerge.ts does the folding.
 *
 * Linking assignments to machines (REQ-03):
 *   An assignment is attached to a ScheduleTask (`scheduleTaskKey`). The machine
 *   bookings for that task also carry the same `scheduleTaskKey` plus a
 *   `machineKey`. We therefore build a `scheduleTaskKey → machineKey[]` index
 *   from the bookings and nest each assignment under the machine(s) its task is
 *   booked on — specifically under that machine's row for the assignment's OWN
 *   project + work order.
 *
 *   MULTI-MACHINE CHOICE: when an assignment's task spans MULTIPLE machines we
 *   nest the assignment under its PRIMARY machine only — the machine with the
 *   lowest machineCode (deterministic). This is required to honour the CRITICAL
 *   invariant below: Highcharts treegrid demands globally-unique point ids, so
 *   an `assign-{key}` bar must appear exactly once. Duplicating it under every
 *   machine would create duplicate ids (Highcharts would silently drop bars and
 *   conflict/deep-link keying would double-count). The bar's start/end span the
 *   whole assignment, so a single primary placement loses no scheduling info.
 *
 *   CATCH-ALL: an assignment whose `scheduleTaskKey` matches no machine booking
 *   (null key, or a task with no machine) can't be linked. Those fall back to a
 *   small top-level "Unassigned to machine" People band so they are never lost.
 *
 * CRITICAL INVARIANT (REQ-03):
 *   The individual booking / assignment bars are built by the SAME
 *   buildBookingPoint / buildAssignmentPoint functions used by the project-
 *   grouped builder. Their `id` and `custom` keys are therefore byte-for-byte
 *   identical to the project-grouped output. Only the PARENT rows differ. This
 *   keeps conflict tagging (ganttConflictTagger keys off taskType +
 *   custom.bookingKey) and deep-links (masterGanttDeepLinks keys off taskType +
 *   custom fields) working unchanged. The bar identities are NOT touched here.
 *
 * Because the Work Order is now its own row, the bar labels NO LONGER carry the
 * WO-number suffix — we pass `undefined` for the optional `woNumber` arg on the
 * bar builders. Only the bar's display `name` changes; its `id`/`custom` are
 * untouched, so the invariant above still holds.
 *
 * Envelope rows reuse the project/workOrder/staff group fills + opacities and
 * the BUG-2026-0095 inclusive→exclusive end-of-day handling from the main
 * builder.
 *
 * The pivot + grouping helpers (collectGroups, the row-id scheme, sort orders,
 * primary-machine selection, date-span helpers) live in the sibling
 * masterGanttMachineGrouping.ts — split out to keep both files under the
 * 600-line CODING-STANDARDS cap.
 */

import type { MasterGanttResponse } from '../../types/schedule.js';

import {
  resolvePointFill,
  type GanttFill,
  type GanttTaskType,
} from './ganttFillResolver.js';

import {
  buildBookingPoint,
  resolveOpacity,
  toMs,
  toEndMs,
  minDate,
  maxDate,
  addDays,
  type GanttPoint,
  type GanttColorTokens,
} from './masterGanttRowBuilder.js';

import {
  groupAssignmentsByPerson,
  mergeAssignmentsByTask,
  buildMergedAssignmentPoint,
  personRowLabel,
  type PersonTaskGroup,
} from './masterGanttAssignmentMerge.js';

import { buildPrepPointsForBucket } from './masterGanttPrepMerge.js';

import {
  collectGroups,
  sortedProjects,
  sortedWorkOrders,
  workOrderStarts,
  workOrderEnds,
  machineRowId,
  machineProjectRowId,
  machineWorkOrderRowId,
  machineWorkOrderPeopleRowId,
  machineWorkOrderPersonRowId,
  personRowId,
  PEOPLE_ROOT_ID,
  type MachineGroup,
  type MachineProjectGroup,
  type MachineWorkOrderGroup,
  type PersonGroup,
} from './masterGanttMachineGrouping.js';

// ---------------------------------------------------------------------------
// Envelope row builders (machine → project → work order → People)
// ---------------------------------------------------------------------------

/**
 * Top-level Machine envelope row: spans the full inclusive range of every
 * booking AND assignment nested anywhere beneath it. Reuses the machinesGroup
 * fill/opacity so it reads as a machine-time band header.
 */
function buildMachineRow(machine: MachineGroup, colors: GanttColorTokens): GanttPoint {
  const starts: (string | null | undefined)[] = [];
  const ends: (string | null | undefined)[] = [];
  for (const project of machine.projects.values()) {
    for (const wo of project.workOrders.values()) {
      starts.push(...workOrderStarts(wo));
      ends.push(...workOrderEnds(wo));
    }
  }

  const taskType: GanttTaskType = 'machinesGroup';
  const fill: GanttFill = resolvePointFill(taskType, null, null, colors);

  const name = machine.machineName
    ? `${machine.machineCode} — ${machine.machineName}`
    : machine.machineCode;

  return {
    id: machineRowId(machine.machineKey),
    name,
    start: toMs(minDate(...starts) ?? undefined),
    // BUG-2026-0095: exclusive end-of-day so the envelope covers the full
    // inclusive range of its child bookings.
    end: toEndMs(maxDate(...ends) ?? undefined),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: false,
    taskType,
    status: null,
    eventType: null,
    custom: { machineKey: machine.machineKey },
  };
}

/**
 * Per-machine Project envelope row, child of a machine row. Spans every booking
 * + assignment in this project on this machine. Reuses the project fill/opacity.
 */
function buildMachineProjectRow(
  machineKey: string,
  project: MachineProjectGroup,
  colors: GanttColorTokens,
): GanttPoint {
  const starts: (string | null | undefined)[] = [];
  const ends: (string | null | undefined)[] = [];
  for (const wo of project.workOrders.values()) {
    starts.push(...workOrderStarts(wo));
    ends.push(...workOrderEnds(wo));
  }

  const taskType: GanttTaskType = 'project';
  const fill: GanttFill = resolvePointFill(taskType, null, null, colors);

  return {
    id: machineProjectRowId(machineKey, project.projectKey),
    parent: machineRowId(machineKey),
    name: project.projectName || project.projectId,
    start: toMs(minDate(...starts) ?? undefined),
    end: toEndMs(maxDate(...ends) ?? undefined),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: false,
    taskType,
    status: null,
    eventType: null,
    custom: { projectKey: project.projectKey },
  };
}

/**
 * Per-machine Work Order envelope row, child of a per-machine project row.
 * Spans the WO's bookings + assignments on this machine. Reuses the workOrder
 * fill/opacity.
 */
function buildMachineWorkOrderRow(
  machineKey: string,
  projectKey: string,
  wo: MachineWorkOrderGroup,
  colors: GanttColorTokens,
): GanttPoint {
  const taskType: GanttTaskType = 'workOrder';
  const fill: GanttFill = resolvePointFill(taskType, null, null, colors);

  const name = wo.workOrderNumber
    ? `WO ${wo.workOrderNumber}${wo.workOrderName ? ` — ${wo.workOrderName}` : ''}`
    : wo.workOrderName || 'Work Order';

  return {
    id: machineWorkOrderRowId(machineKey, wo.workOrderKey),
    parent: machineProjectRowId(machineKey, projectKey),
    name,
    start: toMs(minDate(...workOrderStarts(wo)) ?? undefined),
    end: toEndMs(maxDate(...workOrderEnds(wo)) ?? undefined),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: false,
    taskType,
    status: null,
    eventType: null,
    custom: { workOrderKey: wo.workOrderKey },
  };
}

/**
 * Per-work-order "People" sub-group row, child of a per-machine WO row. Groups
 * the assignments of everyone who worked that WO on this machine. Reuses the
 * staffGroup fill/opacity so it reads as a staff band beneath the WO's bars.
 */
function buildMachineWorkOrderPeopleRow(
  machineKey: string,
  wo: MachineWorkOrderGroup,
  colors: GanttColorTokens,
): GanttPoint {
  const starts = wo.people.map(ea => ea.startDate ?? ea.weekStart);
  const ends = wo.people.map(ea => ea.endDate ?? addDays(ea.weekStart, 6));

  const taskType: GanttTaskType = 'staffGroup';
  const fill: GanttFill = resolvePointFill(taskType, null, null, colors);

  return {
    id: machineWorkOrderPeopleRowId(machineKey, wo.workOrderKey),
    parent: machineWorkOrderRowId(machineKey, wo.workOrderKey),
    name: 'People',
    start: toMs(minDate(...starts) ?? undefined),
    end: toEndMs(maxDate(...ends) ?? undefined),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: false,
    taskType,
    status: null,
    eventType: null,
    custom: { machineKey, workOrderKey: wo.workOrderKey },
  };
}

/**
 * BUG-2026-0194 — per-person summary row under a machine's WO People band.
 * Envelope = every week the person is on this machine for this WO; label
 * "Name · N tasks · S%". Starts collapsed (Highcharts honours `collapsed` on
 * first render only, so auto-refresh never undoes a user's expand).
 */
function buildMachineWorkOrderPersonRow(
  machineKey: string,
  workOrderKey: string,
  person: PersonTaskGroup,
  colors: GanttColorTokens,
): GanttPoint {
  const taskType: GanttTaskType = 'personSummary';
  const fill: GanttFill = resolvePointFill(taskType, null, null, colors);
  return {
    id: machineWorkOrderPersonRowId(machineKey, workOrderKey, person.userKey),
    parent: machineWorkOrderPeopleRowId(machineKey, workOrderKey),
    name: personRowLabel(person),
    start: toMs(person.startDate),
    end: toEndMs(person.endDate),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: true,
    taskType,
    status: null,
    eventType: null,
    custom: { machineKey, workOrderKey, userKey: person.userKey },
  };
}

/**
 * Top-level catch-all "People (unassigned to machine)" band header. Only
 * rendered when at least one assignment couldn't be linked to a machine.
 */
function buildUnassignedBandRow(people: PersonGroup[], colors: GanttColorTokens): GanttPoint {
  const allStarts: (string | null | undefined)[] = [];
  const allEnds: (string | null | undefined)[] = [];
  for (const person of people) {
    for (const ea of person.assignments) {
      allStarts.push(ea.startDate ?? ea.weekStart);
      allEnds.push(ea.endDate ?? addDays(ea.weekStart, 6));
    }
  }

  const taskType: GanttTaskType = 'staffGroup';
  const fill: GanttFill = resolvePointFill(taskType, null, null, colors);

  return {
    id: PEOPLE_ROOT_ID,
    name: 'People (unassigned to machine)',
    start: toMs(minDate(...allStarts) ?? undefined),
    end: toEndMs(maxDate(...allEnds) ?? undefined),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: false,
    taskType,
    status: null,
    eventType: null,
  };
}

/**
 * Per-person envelope row, child of the catch-all band. Spans all of that
 * person's unlinked assignments.
 */
function buildPersonRow(group: PersonGroup, colors: GanttColorTokens): GanttPoint {
  const starts = group.assignments.map(ea => ea.startDate ?? ea.weekStart);
  const ends = group.assignments.map(ea => ea.endDate ?? addDays(ea.weekStart, 6));

  const taskType: GanttTaskType = 'staffGroup';
  const fill: GanttFill = resolvePointFill(taskType, null, null, colors);
  // BUG-2026-0194: "Name · N tasks · S%" like the other person rows.
  const [summary] = groupAssignmentsByPerson(group.assignments);

  return {
    id: personRowId(group.userKey),
    parent: PEOPLE_ROOT_ID,
    name: summary ? personRowLabel(summary) : group.displayName,
    start: toMs(minDate(...starts) ?? undefined),
    end: toEndMs(maxDate(...ends) ?? undefined),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed: false,
    taskType,
    status: null,
    eventType: null,
    custom: { userKey: group.userKey },
  };
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Build the machine-grouped Gantt points (REQ-03).
 *
 * Layout: one Machine row per machineKey → a Project row per project that has
 * work on that machine → a Work Order row per WO → that WO's booking bars, then
 * a per-WO "People" sub-group holding the assignment bars. A small top-level
 * "People (unassigned to machine)" band catches any assignment that can't be
 * linked to a machine. Individual bars are produced by the shared bar builders,
 * so their `id` / `custom` keys are identical to the project-grouped output.
 *
 * @param response  API response from GET /api/schedule/master-gantt
 * @param colors    Theme color tokens (from theme.schedule.*)
 */
export function buildMachineGroupedPoints(
  response: MasterGanttResponse,
  colors: GanttColorTokens,
): GanttPoint[] {
  const { machines, unlinked } = collectGroups(response);
  const points: GanttPoint[] = [];

  // ── Machine band: Machine → Project → Work Order → bookings + People ───────
  for (const machine of machines) {
    points.push(buildMachineRow(machine, colors));

    for (const project of sortedProjects(machine)) {
      points.push(buildMachineProjectRow(machine.machineKey, project, colors));

      for (const wo of sortedWorkOrders(project)) {
        const woRowId = machineWorkOrderRowId(machine.machineKey, wo.workOrderKey);
        points.push(buildMachineWorkOrderRow(machine.machineKey, project.projectKey, wo, colors));

        // Bookings FIRST, directly under the WO row. Reuse buildBookingPoint →
        // identical id ("booking-{bookingKey}") + custom keys; only the parent
        // changes. Drop the WO-number label suffix (the WO is now its own row).
        // BUG-2026-0193: this machine's per-line prep bookings for the WO fold
        // into one "Preparation" bar (masterGanttPrepMerge); tests stay 1:1.
        const { prepPoints, rest } = buildPrepPointsForBucket(wo.bookings, woRowId, colors, true);
        points.push(...prepPoints);
        for (const booking of rest) {
          points.push(buildBookingPoint(booking, woRowId, colors));
        }

        // People sub-group AFTER the bookings. Treegrid renders siblings in
        // data-array order, so the People band sits below the WO's bars.
        // BUG-2026-0194: one row per PERSON (envelope over all their weeks on
        // this machine/WO), drilling down into one merged bar per task. A
        // person with a single task is that task bar directly (no duplicate
        // level). Bars come from buildMergedAssignmentPoint → the shared
        // buildAssignmentPoint, so id ("assign-{firstWeeklyKey}") + custom keys
        // stay compatible with conflict tagging / deep-links; the folded weekly
        // keys ride along in custom.assignmentKeys.
        if (wo.people.length > 0) {
          const peopleRowId = machineWorkOrderPeopleRowId(machine.machineKey, wo.workOrderKey);
          points.push(buildMachineWorkOrderPeopleRow(machine.machineKey, wo, colors));
          for (const person of groupAssignmentsByPerson(wo.people)) {
            if (person.tasks.length === 1) {
              points.push(buildMergedAssignmentPoint(person.tasks[0], peopleRowId, colors));
              continue;
            }
            const personId = machineWorkOrderPersonRowId(machine.machineKey, wo.workOrderKey, person.userKey);
            points.push(buildMachineWorkOrderPersonRow(machine.machineKey, wo.workOrderKey, person, colors));
            for (const task of person.tasks) {
              points.push(buildMergedAssignmentPoint(task, personId, colors));
            }
          }
        }
      }
    }
  }

  // ── Catch-all band: assignments that can't be linked to any machine ────────
  if (unlinked.length > 0) {
    points.push(buildUnassignedBandRow(unlinked, colors));
    for (const group of unlinked) {
      const rowId = personRowId(group.userKey);
      points.push(buildPersonRow(group, colors));
      // BUG-2026-0194: one bar per task (weekly rows merged), not one per week.
      for (const task of mergeAssignmentsByTask(group.assignments)) {
        points.push(buildMergedAssignmentPoint(task, rowId, colors));
      }
    }
  }

  return points;
}
